import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { useContext, useEffect, useState } from 'react';
import { Attribute, AttributeCode, useModelService } from 'services/model/model.service';
import { useNavigate, useParams } from 'react-router-dom';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { ExtendedAttributeCode } from 'pages/trading/model';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import {
  useDrillDownKpiData,
  useEntriesDemandReportData,
  useMerchantDemandReportData,
  useProductCategory,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { CategoryTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/CategoryTable';
import { ProductTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductTable';
import {
  DemandTableRow,
  digestData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { MetricsChart } from 'components/MetricsChart';
import { LineChartConfig } from 'components/chart/data/LineChartConfig';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { InterpretationValue } from 'models/measureInterpretation';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { getChartColor } from 'components/chart/data/ChartColors';
import { FormattedMessage, useIntl } from 'react-intl';
import { addDays, subDays, subWeeks } from 'date-fns';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useTranslatedAttribute } from '../../../../../i18n/translateAttributeHook';
import { MeasureCard } from 'components/MeasureCard';
import { PseudoMeasureCard } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/PseudoMeasureCard';
import { MarketplaceTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/MarketplaceTable';
import { CountryTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/CountryTable';
import { BrandTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/BrandTable';
import { StyleTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/StyleTable';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { ToggleButtonGroup } from '@mui/material';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { PageRefTile } from 'components/Layout/Tiles/PageRefTile';
import { trackMatomoEvent } from 'services/matomo.service';
import { withSuppressedUrlPersistence } from 'hooks/state/UrlPersistence';
import { FilterAttributeCode } from 'hooks/state/DisabledFiltersSlice';
import { DrillDownTitle } from 'components/DrillDownTitle';
import { useAppStore } from 'hooks/state/useAppStore';
import { CountryCodeType } from 'models/countries';
import { tbColors } from 'styles/colors';
import { getTrackingPage, MATOMO_ACTIONS } from 'utils/matomoTracking';
import { getAttributeToggleTrackingName } from 'pages/entities/entities';
import { categoryLink, productLink } from 'pages/entities/entityhelper';

interface GenericKeyedTableRow extends DemandTableRow {
  key: string;
}

const MATOMO_CATEGORY_EXPLORE_MORE = 'Explore more';

export function DemandDetailPage() {
  const intl = useIntl();
  const { setDateRange, setProducts, setCategories, setMarketplaces, setBrands, setCountries, setColors } =
    useAppStore();
  const navigate = useNavigate();
  const { attributeType, attributeValue } = useParams<{ attributeType: AttributeCode; attributeValue: string }>();
  const { setDetailPageBreadcrumb, setBreadcrumbs } = useBreadcrumbStore();
  const [filters, setFilters] = useState<Record<ExtendedAttributeCode, FilterValue[]>>(
    {} as Record<ExtendedAttributeCode, FilterValue[]>
  );

  const getFilterCodes = () => Object.getOwnPropertyNames(filters) as FilterAttributeCode[];

  const { getMeasureByMetric, getAttributes } = useModelService();
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');
  const ordQty = getMeasureByMetric('ordQty', 'OD');

  const [keyAttribute] = getAttributes([attributeType!]);

  const { data: merchantData } = useMerchantDemandReportData();

  const [pageDataTableRow, setPageDataTableRow] = useState<GenericKeyedTableRow>();

  const translatedAttributeValue = useTranslatedAttribute(attributeType ?? '', attributeValue ?? '');

  const transitiveCacheStore = useTransitiveCacheStore();

  const [prdCategory, prdNumber, chnMarketplace, chnCountry, prdBrand, styBrandColor] = getAttributes([
    'prdCategory',
    'prdNumber',
    'chnMarketplace',
    'chnCountry',
    'prdBrand',
    'styBrandColor',
  ]);
  const [availableAttributeTables, setAvailableAttributeTables] = useState<Attribute[]>();
  const [selectedAttributeTable, setSelectedAttributeTable] = useState<Attribute>();

  const attributeToEntityKey: Record<string, string> = {
    prdCategory: 'category',
    prdNumber: 'product',
    chnMarketplace: 'marketplace',
    chnCountry: 'country',
    prdBrand: 'brand',
    styBrandColor: 'style',
  };

  const { data: entriesDemandData } = useEntriesDemandReportData(attributeType ?? '', filters);

  const { data: kpiData } = useDrillDownKpiData(filters);
  const { data: category } = useProductCategory(filters);

  const categoryName = useTranslatedAttribute('prdCategory', category ?? '');
  const productName = useTranslatedAttribute('prdNumber', filters.prdNumber ? (filters.prdNumber[0] as string) : '');

  useEffect(() => {
    if (attributeValue && attributeType) {
      const breadCrumbFilters = setDetailPageBreadcrumb(location.pathname, [
        { attribute: attributeType, values: [attributeValue] },
      ]);
      setFilters(breadCrumbFilters);
    }
  }, [attributeValue, location.pathname, setDetailPageBreadcrumb]);

  const hasProductFilter = getFilterCodes().includes('prdNumber');
  const isProductPage = getFilterCodes()[0] === 'prdNumber';

  useEffect(() => {
    const availableTables = (
      hasProductFilter ? [chnMarketplace, chnCountry, styBrandColor] : [chnMarketplace, chnCountry, prdBrand, prdNumber]
    ).filter(attribute => !getFilterCodes().includes(attribute.code));

    setAvailableAttributeTables(availableTables);
    transitiveCacheStore.setDisabledFilters(getFilterCodes());
  }, [attributeType, filters]);

  useEffect(() => {
    setSelectedAttributeTable(
      availableAttributeTables && availableAttributeTables.length > 0 ? availableAttributeTables[0] : undefined
    );
  }, [availableAttributeTables]);

  const { name: merchantName, currentDataVersion } = useContext(TenantConfigContext);
  const createWeekLabel = (index: number) => {
    if (!currentDataVersion) return '';
    const startDate = subWeeks(currentDataVersion, 4 - index);
    const endDate = addDays(startDate, 6);

    return intl.formatDateTimeRange(startDate, endDate);
  };

  const createLineRenderer = (categoryDataArrays: number[][]) => {
    const chartConfig = LineChartConfig.forCustomMeasure(
      'growth',
      intl.formatMessage({ id: 'demand.category.graph.growth' }),
      MeasureUnitCode.PERCENT,
      InterpretationValue.Positive,
      true
    ) as LineChartConfig & { secondaryAsLine: boolean };
    chartConfig.removeOuterSpacing = false;

    const primaryArray = new ChartDataArray(
      ChartDataType.primary,
      '',
      categoryDataArrays[0].map((_growth, index) => new ChartData(createWeekLabel(index), 0))
    );

    const categoryChartDataArray = new ChartDataArray(
      ChartDataType.breakdown,
      `${intl.formatMessage({ id: 'demand.category.graph.growth' })} (${translatedAttributeValue ? translatedAttributeValue : ''})`,
      categoryDataArrays[0].map((growth, index) => new ChartData(createWeekLabel(index), growth))
    );
    categoryChartDataArray.color = getChartColor(0, false);

    const merchantChartDataArray = new ChartDataArray(
      ChartDataType.breakdown,
      `${intl.formatMessage({ id: 'demand.category.graph.growth' })} (${merchantName})`,
      categoryDataArrays[1].map((growth, index) => new ChartData(createWeekLabel(index), growth))
    );
    merchantChartDataArray.color = tbColors.secondary['700'];
    merchantChartDataArray.dashed = true;

    chartConfig.data = [primaryArray, categoryChartDataArray, merchantChartDataArray];
    chartConfig.secondaryAsLine = false;
    chartConfig.showTooltips = false;

    return new LineChartRenderer(chartConfig);
  };

  useEffect(() => {
    if (entriesDemandData && filters) {
      digestData(
        merchantData,
        entriesDemandData,
        keyAttribute,
        ordGmvVal,
        ordQty,
        entry => ({
          key: keyAttribute.getValue(entry.attributes),
        }),

        tableRows => setPageDataTableRow(tableRows.length > 0 ? tableRows[0] : undefined)
      );
    }
  }, [entriesDemandData, merchantData, keyAttribute]);

  const previousWeekLabel = intl.formatMessage({ id: 'demand.drilldown.comparison.period.previous.week' });

  const onTableToggle = (_event: React.MouseEvent<HTMLElement>, value: Attribute | null) => {
    if (!value || value.code === selectedAttributeTable?.code) {
      return;
    }

    trackMatomoEvent({
      category: getTrackingPage(location.pathname),
      action: MATOMO_ACTIONS.TABLE_TOGGLE,
      name: getAttributeToggleTrackingName(value.code),
    });
    setSelectedAttributeTable(value);
  };

  const getAccountNumberParam = () => {
    const accountNumber = new URLSearchParams(location.search).get('accountNumber');
    return accountNumber ? `?accountNumber=${accountNumber}` : '';
  };

  const setDateRangeFilterToLast7DaysAndPreviousPeriod = () => {
    const today = new Date();
    setDateRange({
      range: {
        presetValue: 'last_7_days',
        from: subDays(today, 7),
        to: subDays(today, 1),
        presetLabel: 'datepicker.options.main.last.thirty',
        default: true,
      },
      comparison: {
        presetValue: 'previous_period',
        from: subDays(today, 14),
        to: subDays(today, 8),
        presetLabel: 'datepicker.options.comparison.previous.year.week',
      },
    });
  };

  const handleTopSellersClick = () => {
    trackMatomoEvent({ category: MATOMO_CATEGORY_EXPLORE_MORE, action: 'Most sold products' });

    navigate(`/insights/product/reports/top-selling${getAccountNumberParam()}`);
    withSuppressedUrlPersistence(() => {
      setDateRangeFilterToLast7DaysAndPreviousPeriod();
      if (category) {
        setCategories([category]);
      }
      setBreadcrumbs([]);
    });
  };

  function addAllDrillDownFilters() {
    if (category) {
      setCategories([category]);
    }
    if (filters.chnMarketplace) {
      setMarketplaces([filters.chnMarketplace[0] as string]);
    }
    if (filters.chnCountry) {
      setCountries([filters.chnCountry[0] as CountryCodeType]);
    }
    if (filters.prdBrand) {
      setBrands([filters.prdBrand[0] as string]);
    }
    if (filters.styBrandColor) {
      setColors([filters.styBrandColor[0] as string]);
    }
  }

  const handleProductDetailsClick = () => {
    trackMatomoEvent({ category: MATOMO_CATEGORY_EXPLORE_MORE, action: 'Product details' });
    const productNumber = filters.prdNumber[0] as string;
    navigate(productLink(productNumber, 'performance'));
    withSuppressedUrlPersistence(() => {
      addAllDrillDownFilters();
      setBreadcrumbs([]);
    });
  };

  const handleAVClick = () => {
    trackMatomoEvent({ category: MATOMO_CATEGORY_EXPLORE_MORE, action: 'Article Visibility' });

    navigate(`/articlevisibility${getAccountNumberParam()}`);

    withSuppressedUrlPersistence(() => {
      setBreadcrumbs([]);
      setProducts([filters.prdNumber[0] as string]);
    });
  };

  const handleCategoryDetailClick = () => {
    trackMatomoEvent({ category: MATOMO_CATEGORY_EXPLORE_MORE, action: 'Category' });

    navigate(categoryLink(category ?? '', 'performance'));
    withSuppressedUrlPersistence(() => {
      addAllDrillDownFilters();
      setBreadcrumbs([]);
    });
  };

  const handleChannelContributionClick = () => {
    trackMatomoEvent({ category: MATOMO_CATEGORY_EXPLORE_MORE, action: 'Channel Contribution' });

    navigate(`/insights/marketplace/reports/channel-breakdown${getAccountNumberParam()}`);
    withSuppressedUrlPersistence(() => {
      if (category) {
        setCategories([category]);
      }
      setBreadcrumbs([]);
    });
  };

  return (
    <>
      <PageContainer>
        <PageSectionTitle additionalTitleId="demand.daterange.last.seven.days">
          <DrillDownTitle filterAttributes={filters} />
        </PageSectionTitle>
        {kpiData && (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 w-full xl:col-span-12 col-span-6">
            <MeasureCard
              measure={ordGmvVal}
              totals={kpiData}
              customComparisonPeriod={previousWeekLabel}
              additionalTitleId={'demand.subtitle.last7days'}
            />
            <MeasureCard
              measure={ordQty}
              totals={kpiData}
              hideComparison
              additionalTitleId={'demand.subtitle.last7days'}
            />

            <PseudoMeasureCard
              titleId={'demand.columns.ordQtyWow'}
              percentage={pageDataTableRow ? pageDataTableRow.ordQtyWowPer : 0}
            />
            <PseudoMeasureCard
              titleId={'demand.columns.ordQty3Wow'}
              percentage={pageDataTableRow ? pageDataTableRow.ordQtyGrowthWowThreeWeekAvg : 0}
            />
          </div>
        )}

        {pageDataTableRow && (
          <Tile
            titleId={'demand.category.graph.title'}
            additionalTitle={intl.formatMessage({ id: 'demand.category.graph.subtitle' })}
          >
            {pageDataTableRow.data.length > 0 && (
              <MetricsChart renderer={createLineRenderer(pageDataTableRow.data)} height="h-[256px]" />
            )}
            {pageDataTableRow.data.length === 0 && <div className="font-bold text-2xl text-center">∞</div>}
          </Tile>
        )}

        {availableAttributeTables && availableAttributeTables.length > 1 && (
          <ToggleButtonGroup
            className="mb-auto mt-auto"
            exclusive
            value={selectedAttributeTable ?? ''}
            onChange={onTableToggle}
          >
            {availableAttributeTables.map(option => (
              <StyledToggleButton
                key={option.name}
                size="small"
                className="whitespace-nowrap text-xs"
                value={option}
                sx={{ color: '#1A1A1A' }}
              >
                <FormattedMessage id={`entities.name.singular.${attributeToEntityKey[option.code] ?? option.code}`} />
              </StyledToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        {selectedAttributeTable?.code === prdCategory.code && (
          <CategoryTable merchantData={merchantData ?? undefined} filters={filters} />
        )}
        {selectedAttributeTable?.code === chnMarketplace.code && (
          <MarketplaceTable merchantData={merchantData ?? undefined} filters={filters} />
        )}
        {selectedAttributeTable?.code === chnCountry.code && (
          <CountryTable merchantData={merchantData ?? undefined} filters={filters} />
        )}
        {selectedAttributeTable?.code === prdBrand.code && (
          <BrandTable merchantData={merchantData ?? undefined} filters={filters} />
        )}
        {selectedAttributeTable?.code === prdNumber.code && (
          <ProductTable merchantData={merchantData ?? undefined} filters={filters} />
        )}
        {selectedAttributeTable?.code === styBrandColor.code && (
          <StyleTable merchantData={merchantData ?? undefined} filters={filters} />
        )}

        <PageSectionTitle
          titleId="general.title.explore_more"
          additionalTitle={<span className="text-2xl ml-[-10px]">...</span>}
        />

        {isProductPage && (
          <>
            <PageRefTile
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'demand.next.tile.details.title' }}
              description={{ id: 'demand.next.tile.details.description', values: { productName: productName } }}
              buttonText={{ id: 'demand.next.tile.details.button' }}
              onClick={handleProductDetailsClick}
            />
            <PageRefTile
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'demand.next.tile.topsellers.title' }}
              description={{ id: 'demand.next.tile.topsellers.description', values: { categoryName: categoryName } }}
              buttonText={{ id: 'demand.next.tile.topsellers.button' }}
              onClick={handleTopSellersClick}
            />
            <PageRefTile
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'demand.next.tile.article.visibility.title' }}
              description={{
                id: 'demand.next.tile.article.visibility.description',
                values: { productName: productName },
              }}
              buttonText={{ id: 'demand.next.tile.article.visibility.button' }}
              onClick={handleAVClick}
            />
          </>
        )}
        {!isProductPage && (
          <>
            <PageRefTile
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'demand.next.tile.category.title' }}
              description={{ id: 'demand.next.tile.category.description', values: { categoryName: categoryName } }}
              buttonText={{ id: 'demand.next.tile.category.button' }}
              onClick={handleCategoryDetailClick}
            />
            <PageRefTile
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'demand.next.tile.topsellers.title' }}
              description={{ id: 'demand.next.tile.topsellers.description' }}
              buttonText={{ id: 'demand.next.tile.topsellers.button' }}
              onClick={handleTopSellersClick}
            />
            <PageRefTile
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'demand.next.tile.channel.contribution.title' }}
              description={{ id: 'demand.next.tile.channel.contribution.description' }}
              buttonText={{ id: 'demand.next.tile.channel.contribution.button' }}
              onClick={handleChannelContributionClick}
            />
          </>
        )}
      </PageContainer>
    </>
  );
}
